<script setup lang="ts">
import ElCurrencyInput from '@/components/common/ElCurrencyInput.vue'
import RcPartModelSelect from '@/components/rc/common/RcPartModelSelect.vue'
import RcPartTypeToggleGroup from '@/components/rc/common/RcPartTypeToggleGroup.vue'
import RcSiteSelect from '@/components/rc/common/RcSiteSelect.vue'
import RcSparePartCustomerSelect from '@/components/rc/common/RcSparePartCustomerSelect.vue'
import { useFormValidationState } from '@/composables/form-validation-state'
import { SiteType } from '@/types/core/Site'
import type { IModel } from '@/types/rc/miners'
import { PartType } from '@/types/rc/miners/PartType'
import type { ISparePart } from '@/types/rc/spare-parts'
import type { FormInstance, FormItemInstance, FormItemProp, FormRules } from 'element-plus'
import { range } from 'lodash'
import { computed, nextTick, onMounted, reactive, ref } from 'vue'

export interface ISparePartsForm extends Partial<ISparePart> {
  part_sns: (string | undefined)[]
}

const partBeingAdded = defineModel<ISparePartsForm>({ required: true })

const emit = defineEmits(['add'])

// If fans are selected the SNs are not mandatory
const fanSelected = computed(() => partBeingAdded.value.part_type === PartType.Fan)

const snsAmount = ref<number>(1)
partBeingAdded.value.part_sns = [undefined]

// IModel format for the model select, but translate to ISparePart for the form
const selectedModel = ref<IModel | undefined>()
const handleModelChange = () => {
  if (selectedModel.value) {
    partBeingAdded.value.part_model = selectedModel.value.name
    partBeingAdded.value.part_model_version = selectedModel.value.version
  } else {
    partBeingAdded.value.part_model = undefined
    partBeingAdded.value.part_model_version = undefined
  }
}

// Extend or shrink the SNs array, keeping the existing ones
const onChangeSNsAmount = () => {
  partBeingAdded.value.part_sns = Array.from(
    { length: snsAmount.value },
    (_, i: number) => partBeingAdded.value.part_sns?.[i] || undefined
  )
}

const partSnsRule = computed(() => [
  {
    required: !fanSelected.value,
    validator: (rule: any, SNs: (string | undefined)[]) => {
      return fanSelected.value || SNs.every((v) => !!v?.length)
    },
    message: 'SN is required.',
    trigger: ['blur'],
  },
])

const formRules = reactive<FormRules<ISparePartsForm>>({
  customer: [{ required: true, message: 'Owner is required.', trigger: ['blur', 'change'] }],
  site: [{ required: true, message: 'Site is required.', trigger: ['blur', 'change'] }],
  part_type: [{ required: true, message: 'Type is required.', trigger: ['blur', 'change'] }],
  part_model: [{ required: true, message: 'Model is required.', trigger: ['blur', 'change'] }],
  part_sns: partSnsRule.value,
})

const { onValidate, isFormValid } = useFormValidationState(formRules)

// As the part_type is set by default, validate from starters
onMounted(() => {
  onValidate('part_type', true)
})

// Since we're pixels await from the form rules,
// let's describe what a valid form looks like
export interface IValidPartForm {
  customer: { id: number }
  site: { site_id: string }
  price?: number
  part_type: PartType
  part_model: string
  part_model_version?: string | null
  part_sns: (string | undefined)[]
}

// Reset model when type changes, and update validation state
const partTypeGroupHandle = ref<FormItemInstance>()
const selectedModelHandle = ref()

const onChangePartType = async () => {
  // Tie fan selected to SNs being required
  formRef.value?.clearValidate()
  await nextTick()
  formRules.part_sns = partSnsRule.value

  // Reset model
  selectedModel.value = undefined
  selectedModelHandle.value.clear()

  // Validate the change on the toggle group
  // (otherwise it'll show as validation error)
  partTypeGroupHandle.value?.validate('change')
}

// Notify the parent, which does the actual calls to the BE
const addPart = () => {
  emit('add')
}

// Form reference to validate and reset
const formRef = ref<FormInstance>()

// Exposed to parent
const validate = async () => {
  await formRef.value?.validate()
}

// Exposed to parent
const reset = async () => {
  snsAmount.value = 1
  partBeingAdded.value.part_sns = [undefined]
  selectedModel.value = undefined

  // Manually reset the validation state, otherwise
  // SN will get highlighted red after resetting
  await nextTick()
  await nextTick()
  formRef.value?.clearValidate()
}

defineExpose({ validate, reset })
</script>

<template>
  <el-form
    ref="formRef"
    label-position="top"
    :rules="formRules"
    hide-required-asterisk
    :model="partBeingAdded"
    :validate-on-rule-change="false"
    @validate="(key: FormItemProp, valid: boolean) => onValidate(key as string, valid)"
  >
    <el-scrollbar height="500px">
      <!-- Owner -->
      <el-form-item label="Owner" prop="customer">
        <RcSparePartCustomerSelect v-model="partBeingAdded.customer" />
      </el-form-item>
      <!-- Site -->
      <el-form-item label="Site" prop="site">
        <RcSiteSelect v-model="partBeingAdded.site" :type="SiteType.RepairCenter" />
      </el-form-item>
      <!-- Price -->
      <el-form-item label="Unit Price (optional)" prop="price">
        <ElCurrencyInput v-model="partBeingAdded.price" />
      </el-form-item>
      <!-- Type -->
      <el-form-item ref="partTypeGroupHandle" label="Type" prop="part_type">
        <RcPartTypeToggleGroup v-model="partBeingAdded.part_type" @change="onChangePartType" />
      </el-form-item>
      <!-- Model -->
      <el-form-item label="Model" prop="part_model">
        <el-row>
          <el-col :span="14">
            <RcPartModelSelect
              ref="selectedModelHandle"
              v-model="selectedModel"
              :part-type="partBeingAdded.part_type"
              @update:model-value="handleModelChange"
            />
          </el-col>
          <el-col :span="1" />
          <el-col :span="3">
            <el-input-number v-model="snsAmount" :min="1" @change="onChangeSNsAmount" />
          </el-col>
          <el-col :span="1" />
        </el-row>
      </el-form-item>
      <!-- SN -->
      <el-form-item class="part-sns-section" label="Serial Numbers" prop="part_sns">
        <el-row v-for="n in range(snsAmount)" :key="n">
          <el-col :span="1" />
          <el-col :span="4">#{{ n + 1 }}</el-col>
          <el-col :span="1" />
          <el-col :span="18"><el-input v-model="partBeingAdded.part_sns[n]" placeholder="Enter SN" /></el-col>
        </el-row>
      </el-form-item>
    </el-scrollbar>
    <!-- Button -->
    <el-button type="primary" size="large" :disabled="!isFormValid" @click="addPart">
      {{ snsAmount > 1 ? '+ Add parts' : '+ Add part' }}
    </el-button>
  </el-form>
</template>

<style scoped lang="scss">
.el-form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-spacing-md);

  .part-sns-section {
    width: 100%;

    :deep(.el-form-item__content) {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-spacing-md);
    }
  }

  :deep(.el-button.is-disabled) {
    background-color: var(--fill-pale);
  }
}

:deep(.el-scrollbar__view) {
  padding-right: 12px;
}

:deep(.el-row) {
  width: 100%;
}
</style>
